import DataHandler from '@atpoint/atpjs/data_handler'
import i18n from '@atpoint/atpjs/helpers/i18n'
import { validationHasError } from '@atpoint/atpjs/validation/validationsLib'
import { getFormDefinition, submitForm } from '@forms/api/atp'
import Application from '@forms/components/application'
import { appendHoneypotData } from '@forms/components/honeypot/honeypot_helper'
import { validateEntry } from '@forms/helpers/validations'
import locales from '@forms/translations'
import Confirmation from '@forms/views/confirmation'
import Form from '@forms/views/form'
import { Loading } from '@forms/views/loading'
import NotFound from '@forms/views/not_found'
import { Map as ImmutableMap, List, fromJS } from 'immutable'
import { Component } from 'react'
import { getEnv } from 'swisscard/helpers/env'

import type { PathType } from '@atpoint/atpjs/data_handler'
import type { ApiError } from '@forms/api/atp'
import type { ElementType } from '@forms/views/form'
import type React from 'react'
import type { FormEvent } from 'react'

const steps = {
  form: Form,
  confirmation: Confirmation,
  notFound: NotFound,
  loading: Loading,
}

type WebFormsProps = {
  id: string
  locale: string
}

type DataType = {
  id: string
  name: string
  elements: Array<ElementType>
}

type Element = ImmutableMap<string, any>
type ElementsType = List<Element>

type WebFormsState = {
  step: 'loading' | 'notFound' | 'form' | 'confirmation'
  elements: ElementsType
  id: string | null
  name: string | null
}

i18n.locales = locales

class WebForms extends Component<WebFormsProps, WebFormsState> {
  state: WebFormsState = {
    step: 'loading',
    elements: List(),
    id: null,
    name: null,
  }

  getFormDefinition = () => {
    getFormDefinition(this.props.id).then(this.buildForm, () => {
      this.setState({ step: 'notFound' })
    })
  }

  generateErrorMessage(error: ApiError) {
    return [
      i18n.trans(['errors', Number(error.status) === 403 ? 'uploadBlocked' : 'canNotConnectToServer']),
      ' ',
      error.response ? `${error.status}: ${error.statusText}` : null,
      new Date().toISOString(),
    ]
      .filter(Boolean)
      .join('\n')
  }

  submitForm = (event: FormEvent) => {
    event.preventDefault()
    if (this.validate()) {
      submitForm(this.props.id, this.createFormData())
        .then(this.submitResolve, this.submitReject)
        .catch((error: ApiError) => {
          alert(this.generateErrorMessage(error))
        })
    }
  }

  buildForm = (data: DataType) => {
    this.setState({ ...data, step: 'form', elements: fromJS(data.elements) as ElementsType })
  }

  submitResolve = (_data: DataType) => {
    const rootElement = document.querySelector(getEnv('ROOT_ELEMENT_SELECTOR') || '')
    if (rootElement) window.scrollTo(0, rootElement.getBoundingClientRect().top + window.scrollY)
    this.setState({ step: 'confirmation' })
  }

  submitReject = (error: ApiError) => {
    if (error.errors) {
      this.scrollToFirstElementWithError()

      let { elements } = this.state

      if (elements == null) {
        return
      }

      error.errors.then((errors) => {
        for (const key of Object.keys(errors)) {
          const match = key.match(/^elements\[(\d)\]\.value$/)
          const index = match && Number(match[1])
          const error = errors[key][0]

          if (error instanceof Object) {
            elements = elements.setIn(
              [index, 'errorMessage'],
              fromJS(
                Object.keys(error).reduce(
                  (obj: { [key: string]: any }, subKey) => {
                    obj[subKey] = error[subKey][0]
                    return obj
                  },
                  {} as { [key: string]: any },
                ),
              ),
            )
          } else {
            elements = elements.setIn([index, 'errorMessage'], error)
          }
        }

        this.setState({ elements })
      })
    } else {
      alert(this.generateErrorMessage(error))
    }
  }

  updateFormElement = (path: PathType, value: any) => {
    const { elements } = this.state

    if (elements == null) {
      return
    }

    const newElements = elements.setIn(path, value) as ElementsType
    this.setState({ elements: newElements }, () => {
      if (elements.some((element: Element) => !!element.get('errorMessage'))) {
        this.validate()
      }
    })
  }

  validate() {
    let { elements } = this.state

    if (elements == null) {
      return true
    }

    elements = elements.map((element: Element) => element.set('errorMessage', validateEntry(element)))

    const hasError = elements.some((element: Element) => {
      const errorMessage = element.get('errorMessage')
      return validationHasError(ImmutableMap.isMap(errorMessage) ? errorMessage.toJS() : errorMessage)
    })

    this.setState({ elements })
    return !hasError
  }

  createNestedFormDataPairs(formData: FormData, item: any, path: string[]) {
    item.forEach((subItem: any, subKey: string) => {
      if (ImmutableMap.isMap(subItem)) {
        this.createNestedFormDataPairs(formData, subItem, [...path, subKey])
      } else {
        formData.append([...path, `[${subKey}]`].join(''), subItem)
      }
    })
  }

  createFormData() {
    const { locale } = this.props
    const { elements } = this.state
    const formData = new FormData()

    elements.forEach((element, index) => {
      const value = element.get('value')
      const name = `web_form[${index}]`

      if (value) {
        if (List.isList(value)) {
          for (const valueListItem of value) {
            formData.append(`${name}[]`, valueListItem as string | Blob)
          }
        } else if (ImmutableMap.isMap(value)) {
          this.createNestedFormDataPairs(formData, value, [name])
        } else {
          formData.append(name, value)
        }
      }
    })

    formData.append('locale', locale)

    appendHoneypotData(formData)

    return formData
  }

  scrollToFirstElementWithError() {
    const firstElementWithError = document.querySelector('.has-error')
    if (firstElementWithError != null) firstElementWithError.scrollIntoView()
  }

  componentDidMount() {
    this.getFormDefinition()
  }

  componentDidUpdate(prevProps: WebFormsProps) {
    if (this.props.id !== prevProps.id) {
      this.getFormDefinition()
    }
  }

  render() {
    const { step, ...other } = this.state
    // NOTE: this type definition is not resolved correctly, therefore cast as React.ElementType.
    // might be solved in future versions of typescript. (typeof Form | typeof Confirmation | typeof NotFound | typeof Loading)
    const Step = steps[step] as React.ElementType
    const formProps = step === 'form' ? { onSubmit: this.submitForm, updater: this.updateFormElement } : {}
    const data = DataHandler.instance(ImmutableMap({}))

    return (
      <Application>
        <Step data={data} {...other} {...formProps} />
      </Application>
    )
  }
}

export default WebForms
